import React from 'react'
import { connect } from 'react-redux'

import { ProjectIdentity, RootStateTypeExtra } from '@store/types'

import { BlockList } from '@components/compare-property/area-list'
import ComparablePropertyItemHeader from '@components/compare-property/comparable-property-item/comparable-property-item-header'
import { UnitsList } from '@components/compare-property/properties-list'
import { AreaListSkeleton } from '@components/compare-property/skeleton'
import DataHandler, { Status } from '@components/data-handler/data-handler'

import { useGetBuildingQuerySilent } from '@api/building'

import { useCompareProperty } from '@adUtilities/components/compare-property/utils'
import { Level, Unit, UnitProperty } from '@adUtilities/types'

interface UnitSelectorProps {
  projectIdentity: ProjectIdentity
  unit?: UnitProperty
  unitIndex?: number
  comparePropertyProps: ReturnType<typeof useCompareProperty>
  onSelectBlock?: (id: string) => void
  activeBlock?: string
  onToggleList?: (state: boolean) => void
}

function UnitSelector({
  projectIdentity,
  unit,
  unitIndex,
  comparePropertyProps,
  onSelectBlock,
  activeBlock,
  onToggleList,
}: UnitSelectorProps) {
  const [showBlockList, setShowBlockList] = React.useState(false)
  const [showPropertiesList, setShowPropertiesList] = React.useState(
    unitIndex === undefined
  )

  const buildingQueryResponse = useGetBuildingQuerySilent({
    projectName: projectIdentity.projectId,
  })

  const blocks = React.useMemo<string[]>(() => {
    if (buildingQueryResponse.status !== Status.FULFILLED) {
      return []
    }
    const { levels } = buildingQueryResponse
    let result: Array<string> = []

    if (buildingQueryResponse.blockOrders.length > 1) {
      result = buildingQueryResponse.blockOrders
    } else {
      levels.forEach((level: Level) => {
        level.data.forEach((unit: Unit) => {
          const { blockId } = unit
          if (blockId && !result.includes(blockId)) {
            result.push(blockId)
          }
        })
      })
    }
    return result
  }, [buildingQueryResponse.status])

  const getSelectedBlock = React.useCallback(() => {
    if (unitIndex === undefined) {
      return activeBlock
    }
    const matchedLevel = buildingQueryResponse.levels.find(
      (item) => item.level === unit?.references.levelId
    )
    return (
      matchedLevel?.data?.find((item) => item.name === unit?.data.name)
        ?.blockId || undefined
    )
  }, [buildingQueryResponse, unitIndex, unit, activeBlock])

  const [selectedBlock, setSelectedBlock] = React.useState(getSelectedBlock())

  const resetSelectedBlock = React.useCallback(() => {
    const block = getSelectedBlock()
    if (block) {
      setSelectedBlock(block)
    }
  }, [getSelectedBlock])

  const handleToggleBlockList = React.useCallback(() => {
    setShowBlockList((prevVal) => {
      if (prevVal && unitIndex === undefined) {
        return prevVal
      }
      resetSelectedBlock()
      return !prevVal
    })
    setShowPropertiesList(false)
  }, [unitIndex, resetSelectedBlock])

  const handleSelectBlock = React.useCallback(
    (id: string) => {
      setSelectedBlock(id)
      setShowBlockList(false)
      onSelectBlock?.(id)
      setShowPropertiesList(true)
    },
    [onSelectBlock]
  )

  const handleTogglePropertiesList = React.useCallback(() => {
    setShowPropertiesList((prevVal) => {
      if (prevVal && unitIndex === undefined) {
        return prevVal
      }
      resetSelectedBlock()
      return !prevVal
    })
    setShowBlockList(false)
  }, [unitIndex, resetSelectedBlock])

  React.useEffect(() => {
    onToggleList?.(showBlockList || showPropertiesList)
  }, [showBlockList, showPropertiesList, onToggleList])

  return (
    <>
      <ComparablePropertyItemHeader
        propertyName={unit?.data.name}
        areaChangerIsActive={showBlockList}
        onAreaChangerActionClick={handleToggleBlockList}
        onPropertyChangerActionClick={handleTogglePropertiesList}
        propertyChangerIsActive={showPropertiesList}
        highlightAreaChanger={showBlockList}
        highlightPropertyChanger={showPropertiesList}
        hideAreaChangerAction={blocks.length < 2}
      />
      {showBlockList ? (
        <DataHandler
          payloads={{
            ...buildingQueryResponse,
            data: buildingQueryResponse.levels,
          }}
          skeletonFrame={<AreaListSkeleton />}
        >
          <BlockList
            className="!h-page-body"
            onSelect={handleSelectBlock}
            selectedBlock={selectedBlock}
            blocks={blocks}
          />
        </DataHandler>
      ) : null}
      {showPropertiesList && unitIndex !== undefined ? (
        <UnitsList
          className="!h-page-body"
          comparePropertyProps={comparePropertyProps}
          actionMode="replace"
          replaceIndex={unitIndex}
          onActionComplete={handleTogglePropertiesList}
          selectedBlock={selectedBlock}
        />
      ) : null}
      {showPropertiesList && unitIndex === undefined ? (
        <UnitsList
          className="!h-page-body"
          comparePropertyProps={comparePropertyProps}
          actionMode="add"
          onActionComplete={handleTogglePropertiesList}
          selectedBlock={selectedBlock}
        />
      ) : null}
    </>
  )
}

export default connect(
  ({ projectIdentity, firestore: { session } }: RootStateTypeExtra) => ({
    projectIdentity,
    activeBlock: session?.building?.activeBlock,
  })
)(UnitSelector)
